/**
 * Prompt Node Component
 *
 * Node for generating or inputting prompts.
 * Supports manual input, Agent-assisted, or LLM-assisted generation.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import type { NodeData, PromptInputMode } from '@/types/imageWorkflow';

const INPUT_MODE_LABELS: Record<PromptInputMode, string> = {
  manual: '手动输入',
  agent: 'Agent 生成',
  llm: 'LLM 生成',
};

const PromptNodeComponent = ({ id, data, selected }: NodeProps) => {
  const nodeData = data as NodeData;
  const inputMode = nodeData.input_mode || 'manual';

  return (
    <BaseNode id={id} data={nodeData} type="prompt" selected={selected}>
      <div className="space-y-1.5">
        {/* Input Mode Badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
            {INPUT_MODE_LABELS[inputMode]}
          </span>
        </div>

        {/* Content based on mode */}
        {inputMode === 'manual' && (
          nodeData.prompt ? (
            <div className="line-clamp-3 text-gray-700 text-sm">{String(nodeData.prompt)}</div>
          ) : (
            <div className="text-gray-400 italic text-sm">点击输入描述文本</div>
          )
        )}

        {inputMode === 'agent' && (
          <div className="space-y-1">
            {nodeData.agent_type && (
              <div className="text-xs text-gray-500">
                Agent: <span className="text-gray-700">{String(nodeData.agent_type)}</span>
              </div>
            )}
            {nodeData.user_input ? (
              <div className="line-clamp-2 text-gray-700 text-sm">
                "{String(nodeData.user_input)}"
              </div>
            ) : (
              <div className="text-gray-400 italic text-sm">点击配置 Agent 生成</div>
            )}
          </div>
        )}

        {inputMode === 'llm' && (
          <div className="space-y-1">
            {nodeData.llm_model && (
              <div className="text-xs text-gray-500">
                模型: <span className="text-gray-700">{String(nodeData.llm_model)}</span>
              </div>
            )}
            {nodeData.user_input ? (
              <div className="line-clamp-2 text-gray-700 text-sm">
                "{String(nodeData.user_input)}"
              </div>
            ) : (
              <div className="text-gray-400 italic text-sm">点击配置 LLM 生成</div>
            )}
          </div>
        )}
      </div>
    </BaseNode>
  );
};

export const PromptNode = memo(PromptNodeComponent);
export default PromptNode;
